import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  AlertCircle,
  MessageCircle,
  Mic,
  Users,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { fmtNumber } from "../utils/format";
import { dashboardApi } from "../api/client";
import { useNavigate } from "react-router-dom";

const Skeleton = ({
  width = "100%",
  height = 20,
  borderRadius = 4,
  style = {},
}) => (
  <div
    className="skeleton-box"
    style={{ display: "inline-block", width, height, borderRadius, ...style }}
  />
);

const PAGE_SIZE_OPTIONS = [10, 20, 50];

export default function UserSessions() {
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    page: 1,
    limit: 20,
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  // Aggregate stats — kept across pages by accumulating from the full dataset.
  // Because the backend paginates, we track a separate "stats" state that is
  // only updated on a fresh load (page 1, no carry-over).
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalSessions: 0,
    totalVoice: 0,
    totalText: 0,
    activeUsers: 0,
    maxSessions: 1,
  });

  const load = useCallback(
    (targetPage = 1, targetLimit = limit) => {
      setLoading(true);
      setError(null);
      dashboardApi
        .sessions({ page: targetPage, limit: targetLimit })
        .then((json) => {
          if (!json.success) throw new Error(json.error);
          setData(json.data);
          setPagination(json.pagination);

          // Stats are derived from the full dataset only when fetching page 1
          // (the backend always returns the full aggregated counts in pagination.total).
          // For per-page stat cards we recompute from the current slice only when on page 1.
          if (targetPage === 1) {
            const d = json.data;
            setStats({
              totalUsers: json.pagination.total,
              totalSessions: d.reduce((s, u) => s + u.total_sessions, 0),
              totalVoice: d.reduce((s, u) => s + u.voice_sessions, 0),
              totalText: d.reduce((s, u) => s + u.text_sessions, 0),
              activeUsers: d.filter((u) => u.total_sessions > 0).length,
              maxSessions: Math.max(...d.map((x) => x.total_sessions), 1),
            });
          }
        })
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    },
    [limit],
  );

  // Reset to page 1 whenever search filter changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    load(page, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const handleRefresh = () => {
    setPage(1);
    load(1, limit);
  };

  // Client-side filter on the current page slice
  const filtered = data.filter((u) => {
    const name =
      `${u.firstname} ${u.lastname} ${u.email} ${u.username}`.toLowerCase();
    return !search || name.includes(search.toLowerCase());
  });

  const {
    totalUsers,
    totalSessions,
    totalVoice,
    totalText,
    activeUsers,
    maxSessions,
  } = stats;
  const { total, totalPages } = pagination;

  // Pagination helpers
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const pageNumbers = () => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 4) return [1, 2, 3, 4, 5, "…", totalPages];
    if (page >= totalPages - 3)
      return [
        1,
        "…",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    return [1, "…", page - 1, page, page + 1, "…", totalPages];
  };

  return (
    <div style={{ width: "100%" }}>
      <style>{`
        @keyframes skeleton-loading {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .skeleton-box {
          background: linear-gradient(90deg, var(--ink-100, #f3f4f6) 25%, var(--ink-200, #e5e7eb) 50%, var(--ink-100, #f3f4f6) 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s infinite linear;
        }
        .page-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 32px;
          height: 32px;
          padding: 0 6px;
          border-radius: 8px;
          border: 1px solid var(--ink-200, #e5e7eb);
          background: #fff;
          color: var(--ink-700);
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.15s, border-color 0.15s, color 0.15s;
          user-select: none;
        }
        .page-btn:hover:not(:disabled):not(.active) {
          background: var(--ink-50, #f9fafb);
          border-color: var(--ink-300, #d1d5db);
        }
        .page-btn.active {
          background: var(--saffron, #c26c1b);
          border-color: var(--saffron, #c26c1b);
          color: #fff;
          cursor: default;
        }
        .page-btn:disabled {
          opacity: 0.38;
          cursor: not-allowed;
        }
      `}</style>

      {/* Hero */}
      <div
        style={{ position: "relative", marginBottom: 32, padding: "16px 0" }}
      >
        <div
          className="devanagari-watermark"
          style={{
            position: "absolute",
            top: -30,
            right: 20,
            fontSize: 180,
            background:
              "linear-gradient(135deg, rgba(194,108,27,0.08) 0%, rgba(194,108,27,0.01) 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            pointerEvents: "none",
            userSelect: "none",
            fontWeight: 800,
          }}
        >
          उपयोग
        </div>
        <div
          className="t-eyebrow"
          style={{ color: "var(--ink-500)", letterSpacing: "0.05em" }}
        >
          Sangathana Console / Alpha Tester Conversations
        </div>
        <h1
          className="t-display"
          style={{
            fontSize: 36,
            marginTop: 8,
            marginBottom: 12,
            color: "var(--ink-900)",
          }}
        >
          Alpha tester conversations.
        </h1>
        <p
          style={{
            color: "var(--ink-600)",
            fontFamily: "var(--serif)",
            fontSize: 16,
            fontStyle: "italic",
            maxWidth: 720,
            lineHeight: 1.5,
          }}
        >
          {fmtNumber(totalUsers)} user{totalUsers === 1 ? "" : "s"} ·{" "}
          {fmtNumber(totalSessions)} session{totalSessions === 1 ? "" : "s"}{" "}
          total.
        </p>
      </div>

      {/* Summary cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 16,
          marginBottom: 24,
        }}
      >
        {[
          {
            label: "Total Users",
            value: fmtNumber(totalUsers),
            icon: <Users size={16} />,
            accent: "var(--ink-600)",
          },
          {
            label: "Active Users",
            value: fmtNumber(activeUsers),
            icon: <Users size={16} />,
            accent: "var(--saffron)",
          },
          {
            label: "Text Sessions",
            value: fmtNumber(totalText),
            icon: <MessageCircle size={16} />,
            accent: "var(--blue, #2563eb)",
          },
          {
            label: "Voice Sessions",
            value: fmtNumber(totalVoice),
            icon: <Mic size={16} />,
            accent: "var(--green, #16a34a)",
          },
        ].map(({ label, value, icon, accent }) => (
          <div
            key={label}
            className="panel"
            style={{
              padding: "18px 20px",
              background: "#ffffff",
              borderRadius: 16,
              border: "1px solid var(--ink-100)",
              boxShadow: "0 4px 20px -4px rgba(0,0,0,0.02)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: accent,
                marginBottom: 8,
              }}
            >
              {icon}
              <span
                className="t-eyebrow"
                style={{ color: "var(--ink-500)", letterSpacing: "0.05em" }}
              >
                {label}
              </span>
            </div>
            <div
              style={{ fontSize: 28, fontWeight: 700, color: "var(--ink-900)" }}
            >
              {loading ? <Skeleton width={60} height={28} /> : value}
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div
        className="panel"
        style={{
          padding: "20px 24px",
          marginBottom: 24,
          background: "#ffffff",
          borderRadius: 16,
          border: "1px solid var(--ink-100)",
          boxShadow: "0 4px 20px -4px rgba(0,0,0,0.02)",
        }}
      >
        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: "1fr auto",
            alignItems: "end",
          }}
        >
          <div>
            <label
              className="t-eyebrow"
              style={{
                display: "block",
                marginBottom: 6,
                color: "var(--ink-500)",
              }}
            >
              Search
            </label>
            <div style={{ position: "relative" }}>
              <Search
                size={14}
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--ink-400)",
                }}
              />
              <input
                className="input"
                style={{ paddingLeft: 36 }}
                placeholder="Name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <button
            className="btn"
            onClick={handleRefresh}
            style={{
              padding: "8px 16px",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: "16px 20px",
            borderRadius: 12,
            border: "1px solid var(--red)",
            background: "#fff1f2",
            color: "var(--red)",
            marginBottom: 24,
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontWeight: 500,
          }}
        >
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Table */}
      <div
        className="panel"
        style={{
          background: "#ffffff",
          borderRadius: 16,
          border: "1px solid var(--ink-100)",
          boxShadow: "0 4px 20px -4px rgba(0,0,0,0.02)",
          overflow: "hidden",
        }}
      >
        <table className="table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Role</th>
              <th>Text & Voice</th>
              <th style={{ textAlign: "right" }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {loading &&
              [...Array(limit)].map((_, i) => (
                <tr key={i}>
                  <td>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <Skeleton width={32} height={32} borderRadius={16} />
                      <div>
                        <Skeleton
                          width={100}
                          height={14}
                          style={{ display: "block", marginBottom: 4 }}
                        />
                        <Skeleton
                          width={70}
                          height={11}
                          style={{ display: "block" }}
                        />
                      </div>
                    </div>
                  </td>
                  <td>
                    <Skeleton width={140} height={14} />
                  </td>
                  <td>
                    <Skeleton width={60} height={22} borderRadius={12} />
                  </td>
                  <td>
                    <Skeleton width="100%" height={30} borderRadius={12} />
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <Skeleton width={36} height={20} borderRadius={8} />
                  </td>
                </tr>
              ))}

            {!loading && !filtered.length && (
              <tr>
                <td
                  colSpan={5}
                  style={{
                    padding: "48px 24px",
                    textAlign: "center",
                    color: "var(--ink-400)",
                    fontFamily: "var(--serif)",
                    fontStyle: "italic",
                    fontSize: 16,
                    background: "var(--paper-2)",
                  }}
                >
                  No users match these filters.
                </td>
              </tr>
            )}

            {!loading &&
              filtered.map((u) => {
                const initials =
                  `${u.firstname?.[0] ?? ""}${u.lastname?.[0] ?? ""}`.toUpperCase();
                const textPct = (u.text_sessions / maxSessions) * 100;
                const voicePct = (u.voice_sessions / maxSessions) * 100;

                return (
                  <tr
                    key={u.id}
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate(`/sessions/user/${u.id}`, {
                        state: {
                          displayName: `${u.firstname} ${u.lastname}`,
                          username: u.username,
                        },
                      })
                    }
                    onMouseEnter={(e) =>
                    (e.currentTarget.style.background =
                      "var(--paper-2, #faf8f5)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "")
                    }
                  >
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background: "var(--saffron-tint, #fdf3e7)",
                            color: "var(--saffron-deep, #92560e)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 11,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {initials}
                        </div>
                        <div>
                          <div
                            style={{
                              fontWeight: 600,
                              color: "var(--ink-900)",
                              fontSize: 14,
                            }}
                          >
                            {u.firstname} {u.lastname}
                          </div>
                          <div
                            style={{ fontSize: 11, color: "var(--ink-500)" }}
                          >
                            @{u.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: 13, color: "var(--ink-600)" }}>
                      {u.email}
                    </td>
                    <td>
                      <span
                        className="badge badge-outline"
                        style={{ fontSize: 11 }}
                      >
                        {u.role_in_org}
                      </span>
                    </td>
                    <td style={{ minWidth: 220 }}>
                      {u.total_sessions > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <MessageCircle size={12} style={{ color: "var(--blue, #2563eb)" }} />
                            <div
                              title={`Text: ${u.text_sessions}`}
                              style={{
                                flex: 1,
                                height: 6,
                                borderRadius: 999,
                                background: "var(--ink-100)",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  height: "100%",
                                  width: `${textPct}%`,
                                  background: "var(--blue, #2563eb)",
                                  borderRadius: 999,
                                  transition: "width 0.4s ease",
                                }}
                              />
                            </div>
                            <span style={{ fontSize: 11, color: "var(--ink-500)", minWidth: 32, textAlign: "right" }}>
                              {fmtNumber(u.text_sessions)}
                            </span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <Mic size={12} style={{ color: "var(--green, #16a34a)" }} />
                            <div
                              title={`Voice: ${u.voice_sessions}`}
                              style={{
                                flex: 1,
                                height: 6,
                                borderRadius: 999,
                                background: "var(--ink-100)",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  height: "100%",
                                  width: `${voicePct}%`,
                                  background: "var(--green, #16a34a)",
                                  borderRadius: 999,
                                  transition: "width 0.4s ease",
                                }}
                              />
                            </div>
                            <span style={{ fontSize: 11, color: "var(--ink-500)", minWidth: 32, textAlign: "right" }}>
                              {fmtNumber(u.voice_sessions)}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            height: 13,
                            background: "var(--ink-50)",
                            borderRadius: 3,
                          }}
                        />
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {u.total_sessions > 0 ? (
                        <span className="badge badge-saffron">
                          {fmtNumber(u.total_sessions)}
                        </span>
                      ) : (
                        <span style={{ color: "var(--ink-300)", fontSize: 13 }}>
                          0
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>

        {/* Pagination footer — lives inside the panel, below the table */}
        {!loading && total > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 20px",
              borderTop: "1px solid var(--ink-100)",
            }}
          >
            {/* Left: count info + rows-per-page */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                fontSize: 13,
                color: "var(--ink-500)",
              }}
            >
              <span>
                {fmtNumber((page - 1) * limit + 1)}–
                {fmtNumber(Math.min(page * limit, total))} of {fmtNumber(total)}
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span
                  className="t-eyebrow"
                  style={{ color: "var(--ink-400)", letterSpacing: "0.04em" }}
                >
                  Rows
                </span>
                <select
                  className="select"
                  style={{
                    padding: "3px 8px",
                    fontSize: 13,
                    height: 30,
                    borderRadius: 8,
                  }}
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </span>
            </div>

            {/* Right: page buttons */}
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button
                className="page-btn"
                disabled={!canPrev}
                onClick={() => setPage((p) => p - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft size={14} />
              </button>

              {pageNumbers().map((n, i) =>
                n === "…" ? (
                  <span
                    key={`ellipsis-${i}`}
                    style={{
                      padding: "0 4px",
                      color: "var(--ink-400)",
                      fontSize: 13,
                    }}
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={n}
                    className={`page-btn${n === page ? " active" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                ),
              )}

              <button
                className="page-btn"
                disabled={!canNext}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
